import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";

import type {
    ImageChooserBlock,
    TSIAlignedRichTextBlock,
    PromotionBlock as Value,
    _Column,
} from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useLazyBackground } from "../../utils/lazyBackground";
import { isElementVisible } from "../../utils/visibility";
import { RawHTMLBlock } from "../RawHTMLBlock";
import { getSpacingClasses } from "../_spacing/spacing";

type IProps = Immutable<{
    value: Value;
    /**
     * 1-indexed position of this block in its parent StreamField. Used to
     * derive `creative_slot` for the `view_promotion` GA4 event. Defaults
     * to 1 when the block is rendered standalone (e.g. via the
     * legacy Category A `{% include_react PromotionBlockTemplate %}` path
     * where the position isn't known).
     */
    blockIndex?: number;
}>;

type ColumnBlock = _Column[number];
type ValuePropsValue = Extract<ColumnBlock, { type: "value_props" }>["value"];

/**
 * Resolve the `data-background-image` url and `data-background-image-height`
 * for a `lazy-background` section, mirroring the legacy
 * `{% image ... width-2000 %}` template tag: the lazy-background JS sizes
 * `background--image-full-height` blocks to data-background-image-height,
 * which must be the width-2000 rendition's height (the height the image
 * renders at in a full-bleed column), not the full-resolution original
 * height. Falls back to the original image when the rendition is absent.
 */
const getBackgroundImageData = (
    bgImage: Immutable<ImageChooserBlock> | null | undefined,
): { url: string; height: number | "" } => {
    const rendition = bgImage?.renditions["width-2000"];
    return {
        url: rendition?.url ?? bgImage?.url ?? "",
        height: rendition?.height ?? bgImage?.height ?? "",
    };
};

/**
 * Minimal renderer for TSIAlignedRichTextBlock structs (header/footer of a
 * PromotionBlock). The Django template emits a full <section> with lazy-
 * background data attributes; we mirror that DOM here so existing site CSS
 * and the lazy-background JS continue to work.
 */
const AlignedRichTextBlock = (
    props: Immutable<{ value: TSIAlignedRichTextBlock }>,
): React.JSX.Element | null => {
    const v = props.value;
    const sectionRef = useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    const { url: bgUrl, height: bgHeight } = getBackgroundImageData(
        v.background_style?.background_image,
    );
    const shiftJson = JSON.stringify(
        v.background_style?.background_image_shift ?? null,
    );
    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "aligned-rich-text-block",
                v.alignment ?? "",
                v.full_width ? "aligned-rich-text-block--full-width" : "",
                v.background_style?.style ?? "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
                bgUrl ? "background-image" : "",
                "lazy-background",
            ])}
            data-background-image={bgUrl}
            data-background-image-height={bgHeight}
            data-background-image-shift={shiftJson}
        >
            {/*
             * Bare <div> with suppressHydrationWarning (matching RichTextBlock's
             * output: a classless <div dangerouslySetInnerHTML>) rather than
             * <RichTextBlock>. Aligned-rich-text content commonly embeds
             * third-party widgets -- notably Wistia video embeds -- whose loader
             * scripts rewrite this container's innerHTML before React hydration
             * commits ("external changing data" in React's hydration-mismatch
             * warning). The rewrite is benign (the player renders), so suppress
             * the otherwise-unavoidable warning instead of having React try to
             * reconcile third-party DOM.
             */}
            <div
                suppressHydrationWarning
                dangerouslySetInnerHTML={{ __html: v.content ?? "" }}
            />
        </section>
    );
};

/**
 * Render a column image using the largest available rendition's
 * dimensions instead of the full-res original.
 */
const renderColumnImage = (
    image: Immutable<ImageChooserBlock>,
): React.JSX.Element => {
    const r = image.renditions;
    const widths = [250, 500, 1000, 2000] as const;
    const seenWidths = new Set<number>();
    const srcSet = widths
        .map((w) => {
            const rendition = r[`width-${w}`];
            if (!rendition || seenWidths.has(rendition.width)) {
                return "";
            }
            seenWidths.add(rendition.width);
            // `rendition.width`, not the spec key: Wagtail never upscales,
            // so the actual pixel width can be smaller than the spec, and
            // multiple specs may collapse to the same rendition (which
            // would otherwise produce duplicate `w` descriptors).
            return `${rendition.url} ${rendition.width}w`;
        })
        .filter(Boolean)
        .join(", ");
    const largest =
        r["width-2000"] ?? r["width-1000"] ?? r["width-500"] ?? r["width-250"];
    return (
        <img
            src={largest ? largest.url : image.url}
            srcSet={srcSet || undefined}
            sizes="auto"
            loading="lazy"
            decoding="async"
            alt={image.default_alt_text || image.title}
            width={largest ? largest.width : image.width}
            height={largest ? largest.height : image.height}
        />
    );
};

const ValuePropsRenderer = (
    props: Immutable<{ value: ValuePropsValue }>,
): React.JSX.Element => {
    const icon = props.value.icon;
    return (
        <div className="value-prop">
            {icon && (
                // No width/height attrs: the legacy `{% wagtail_image %}`
                // template rendered the icon without them, and `.value-prop
                // img` only constrains `width` (4.5rem). Setting `height=100`
                // here would survive as a presentational hint (CSS doesn't
                // override height) and render the icon 72x100 instead of the
                // intrinsic-square 72x72, shifting every value-prop down.
                <img
                    src={
                        icon.renditions["fill-100x100"]
                            ? icon.renditions["fill-100x100"].url
                            : icon.url
                    }
                    alt={icon.default_alt_text || icon.title}
                    loading="lazy"
                    decoding="async"
                />
            )}
            <RichTextBlock value={props.value.content ?? ""} />
        </div>
    );
};

type ColumnProps = Immutable<{
    value: _Column;
    onSelectPromotion?: () => void;
}>;

const Column = (props: ColumnProps): React.JSX.Element => (
    <div className="col">
        <StreamField
            tagName={React.Fragment}
            value={props.value}
            components={{
                image: (block) => renderColumnImage(block.value),
                image_with_link: (block) => {
                    const url = block.value.link?.url ?? "";
                    const image = block.value.image;
                    return (
                        <a href={url} onClick={props.onSelectPromotion}>
                            {image && renderColumnImage(image)}
                        </a>
                    );
                },
                aligned_rich_text_block: (block) => (
                    <AlignedRichTextBlock value={block.value} />
                ),
                value_props: (block) => (
                    <ValuePropsRenderer value={block.value} />
                ),
                raw_HTML: (block) => <RawHTMLBlock value={block.value} />,
            }}
        />
    </div>
);

// --- Tracking helpers (port of tsicommon_tracker_tags.py) ---------------------

/**
 * Mirrors `_promotion_block_has_trackable_shared_content` in
 * tsicommon/templatetags/tsicommon_tracker_tags.py: a promotion block is
 * trackable when at least one column contains an `image_with_link` block whose
 * value has a `link` set.
 */
const hasTrackableSharedContent = (
    columns: Immutable<NonNullable<Value["columns"]>>,
): boolean =>
    columns.some((column) =>
        column.some(
            (block) =>
                block.type === "image_with_link" && block.value.link != null,
        ),
    );

const stripHtml = (html: string): string => {
    if (!html) {
        return "";
    }
    const text = html.replace(/<[^>]*>/g, " ");
    return text.replace(/\s+/g, " ").trim();
};

const slugify = (value: string): string =>
    value
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

const extractTextFromAlignedRichText = (
    value: Immutable<TSIAlignedRichTextBlock> | null | undefined,
): string => {
    if (!value) {
        return "";
    }
    return stripHtml(value.content ?? "");
};

const BLOCK_NAME = "promotion-block";
const FALLBACK_NAME = "Promotion";

type PromotionMetadata = {
    creative_name: string;
    promotion_name: string;
    promotion_id_base: string;
};

const buildPromotionMetadata = (value: Immutable<Value>): PromotionMetadata => {
    const headerText = extractTextFromAlignedRichText(value.header_content);
    const resolvedName = headerText || FALLBACK_NAME;
    const idBase =
        slugify(resolvedName) || slugify(BLOCK_NAME) || "tracking-block";
    return {
        creative_name: resolvedName,
        promotion_name: resolvedName,
        promotion_id_base: idBase,
    };
};

const buildPromotionEvent = (
    value: Immutable<Value>,
    index: number,
    eventName: "view_promotion" | "select_promotion",
): Record<string, unknown> => {
    const meta = buildPromotionMetadata(value);
    return {
        event: eventName,
        creative_name: meta.creative_name,
        creative_slot: `${BLOCK_NAME}-${index}`,
        promotion_id: `${meta.promotion_id_base}-${index}`,
        promotion_name: meta.promotion_name,
        // Mirror Django `_build_promotion_event`, which always includes an
        // `items` array (empty when no entries) after `_compact_dict`. Keeps
        // the GA4 ecommerce event shape identical to the legacy schema.
        items: [],
    };
};

const isDoNotTrackEnabled = (win: Window): boolean => {
    const nav = win.navigator as Navigator & {
        msDoNotTrack?: string;
        globalPrivacyControl?: boolean;
    };
    return (
        nav.doNotTrack === "1" ||
        nav.msDoNotTrack === "1" ||
        nav.globalPrivacyControl === true
    );
};

const pushPromotionEvent = (
    value: Immutable<Value>,
    index: number,
    eventName: "view_promotion" | "select_promotion",
): void => {
    const win = getWindow();
    if (!win) {
        return;
    }
    if (isDoNotTrackEnabled(win)) {
        return;
    }
    // Mirror the legacy push_to_datalayer.html guard: GTM may be absent or
    // blocked, so `dataLayer` is not guaranteed to exist before we push.
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ items: null });
    win.dataLayer.push(buildPromotionEvent(value, index, eventName));
};

// --- Main component ----------------------------------------------------------

export const PromotionBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const columns = v.columns ?? [];
    const trackingEnabled = hasTrackableSharedContent(columns);
    const blockIndex = props.blockIndex ?? 1;
    const sectionRef = useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);

    useEffect(() => {
        if (!trackingEnabled) {
            return;
        }
        const win = getWindow();
        if (!win) {
            return;
        }
        // Gate on visibility to mirror the Django {% track_view_promotion %}
        // tag (see push_to_datalayer.html): hidden blocks (e.g. via
        // `responsive_visibility` setting `display: none`) must not emit a
        // `view_promotion` event, otherwise GA4 analytics would be inflated.
        if (!isElementVisible(sectionRef.current)) {
            return;
        }
        pushPromotionEvent(v, blockIndex, "view_promotion");
        // Fire-on-mount only, mirroring the Django {% track_view_promotion %}
        // tag which renders once per page render. The closure intentionally
        // captures `v`, `blockIndex`, and `trackingEnabled` from the initial
        // render; subsequent prop changes must NOT re-fire view_promotion.
        // This codebase does not enable react-hooks/exhaustive-deps, so an
        // eslint-disable annotation would itself be flagged as unused.
    }, []);

    const { url: bgUrl, height: bgHeight } = getBackgroundImageData(
        v.background_style?.background_image,
    );
    const shiftJson = JSON.stringify(
        v.background_style?.background_image_shift ?? null,
    );

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "promotion-block",
                v.theme ?? "",
                v.responsive_visibility?.visibility ?? "",
                v.column_layout ?? "",
                v.full_bleed ? "u-full-bleed" : "l-capped-width",
                v.text_style?.style ?? "",
                v.background_style?.style ?? "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
                bgUrl ? "background-image" : "",
                v.no_dividers ? "no-dividers" : "",
                "lazy-background",
            ])}
            data-background-image={bgUrl}
            data-background-image-height={bgHeight}
            data-background-image-shift={shiftJson}
        >
            <header>
                {v.header_content && (
                    <AlignedRichTextBlock value={v.header_content} />
                )}
            </header>
            <div>
                {columns.map((column, i) => (
                    <Column
                        key={i}
                        value={column}
                        onSelectPromotion={
                            trackingEnabled
                                ? () =>
                                      pushPromotionEvent(
                                          v,
                                          i + 1,
                                          "select_promotion",
                                      )
                                : undefined
                        }
                    />
                ))}
            </div>
            <footer>
                {v.footer_content && (
                    <AlignedRichTextBlock value={v.footer_content} />
                )}
            </footer>
        </section>
    );
};
